import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Clock, XCircle, ArrowRight, Package, Factory, Truck, Home } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONFIG = {
  success: {
    title: 'Pagamento aprovado!',
    subtitle: 'Recebemos seu pagamento. Seu móvel já entrou em produção — você pode acompanhar cada etapa em "Meus Pedidos".',
    icon: CheckCircle2,
    accent: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
  },
  pending: {
    title: 'Pagamento em análise',
    subtitle: 'Seu pagamento está sendo processado. Assim que aprovado, começamos a produção do seu móvel.',
    icon: Clock,
    accent: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  failure: {
    title: 'Pagamento não concluído',
    subtitle: 'Algo deu errado. Você pode tentar novamente ou escolher outra forma de pagamento.',
    icon: XCircle,
    accent: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
  },
};

const Stepper = () => {
  const steps = [
    { Icon: CheckCircle2, label: 'Pago' },
    { Icon: Factory, label: 'Em produção' },
    { Icon: Package, label: 'Preparação' },
    { Icon: Truck, label: 'Enviado' },
    { Icon: Home, label: 'Entregue' },
  ];
  return (
    <div className="flex items-center justify-between gap-2 my-10">
      {steps.map((s, i) => (
        <React.Fragment key={s.label}>
          <div className="flex flex-col items-center flex-1">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
              i <= 1 ? 'bg-neutral-900 border-neutral-900 text-white' : 'bg-white border-neutral-300 text-neutral-400'
            } ${i === 1 ? 'ring-4 ring-neutral-900/10' : ''}`}>
              <s.Icon className="w-4 h-4" strokeWidth={1.5} />
            </div>
            <span className={`text-[10px] uppercase tracking-widest mt-2 text-center ${i <= 1 ? 'text-neutral-900' : 'text-neutral-400'}`}>
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && <div className={`h-px flex-1 -mt-6 ${i < 1 ? 'bg-neutral-900' : 'bg-neutral-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );
};

const CheckoutResult = ({ status }) => {
  const cfg = CONFIG[status] || CONFIG.pending;
  const Icon = cfg.icon;
  usePageTitle(cfg.title);
  const [params] = useSearchParams();
  const orderId = params.get('order_id') || params.get('external_reference');
  const [order, setOrder] = useState(null);
  const { fetchCart } = useCart();

  useEffect(() => {
    // Se sucesso, limpa carrinho local (backend já limpa quando sincroniza)
    if (status === 'success' && fetchCart) {
      fetchCart();
    }
    if (!orderId) return;
    axios
      .get(`${API_URL}/payments/orders/${orderId}`)
      .then((r) => {
        setOrder(r.data);
        // dispara refetch do carrinho após pagamento confirmado
        if (fetchCart && r.data?.payment_status === 'approved') fetchCart();
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, status]);

  const isApproved = status === 'success' || order?.payment_status === 'approved';

  return (
    <div data-testid={`checkout-${status}`} className="min-h-screen bg-white pt-32 pb-24 px-6">
      <div className="max-w-3xl mx-auto text-center">
        <div className={`w-20 h-20 rounded-full ${cfg.bg} ${cfg.border} border mx-auto mb-8 flex items-center justify-center`}>
          <Icon className={`w-10 h-10 ${cfg.accent}`} strokeWidth={1.2} />
        </div>
        <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-neutral-900 mb-4">
          {cfg.title}
        </h1>
        <p className="text-base font-light text-neutral-600 max-w-xl mx-auto mb-4">
          {cfg.subtitle}
        </p>

        {order && (
          <div className="border border-neutral-200 inline-block px-8 py-5 mb-4 mt-6">
            <p className="text-xs text-neutral-500 uppercase tracking-widest mb-1">Pedido</p>
            <p className="font-mono text-sm text-neutral-900 mb-3">#{order.order_id?.slice(0, 8)}</p>
            <p className="text-2xl font-heading text-neutral-900">
              R$ {Number(order.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
          </div>
        )}

        {isApproved && (
          <>
            <div className="border-t border-neutral-200 pt-8 mt-8">
              <p className="font-accent text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-2">
                Status do seu pedido
              </p>
              <p className="text-sm font-light text-neutral-700 mb-2">
                Estamos preparando seu móvel com toda a atenção que ele merece.
              </p>
            </div>
            <Stepper />
          </>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-4">
          {isApproved && (
            <Link
              to="/meus-pedidos"
              data-testid="result-cta-orders"
              className="inline-flex items-center gap-2 bg-neutral-900 text-white px-10 py-4 uppercase tracking-widest text-xs hover:bg-black"
            >
              Ver Meus Pedidos <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          )}
          <Link
            to="/catalogo"
            data-testid="result-cta-catalog"
            className={`inline-flex items-center gap-2 px-10 py-4 uppercase tracking-widest text-xs ${
              isApproved
                ? 'border border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white'
                : 'bg-neutral-900 text-white hover:bg-black'
            }`}
          >
            Continuar Comprando
          </Link>
          {status === 'failure' && (
            <Link
              to="/checkout"
              data-testid="result-cta-retry"
              className="inline-flex items-center gap-2 border border-neutral-900 text-neutral-900 px-10 py-4 uppercase tracking-widest text-xs hover:bg-neutral-900 hover:text-white"
            >
              Tentar Novamente
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutResult;
